import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/base/LanguageSwitcher';
import MobileMenu from '@/components/feature/MobileMenu';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    if (isHomePage) {
      const el = document.getElementById(sectionId);
      if (el) {
        const headerHeight = 72;
        const top = el.getBoundingClientRect().top + window.scrollY - headerHeight;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
  }, [isHomePage, navigate]);

  // Scroll to hash section on home page load (e.g. navigating from another page)
  useEffect(() => {
    if (isHomePage && location.hash) {
      const id = location.hash.replace('#', '');
      // Small delay to let the page render first
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const headerHeight = 72;
          const top = el.getBoundingClientRect().top + window.scrollY - headerHeight;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isHomePage, location.hash]);

  const handleHashClick = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    scrollToSection(sectionId);
  };

  const navLinks = [
    { href: '/#services', sectionId: 'services', label: t('nav.services') },
    { href: '/#register', sectionId: 'register', label: t('nav.register') },
    { href: '/blog', sectionId: null, label: t('nav.blog') },
    { href: '/help-center', sectionId: null, label: t('footer.helpCenter') },
  ];

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled || !isHomePage
            ? 'bg-background-50/95 backdrop-blur-sm border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img src="/icon.svg" alt="5vix" className="h-8 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
              link.sectionId ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleHashClick(e, link.sectionId!)}
                  className="text-foreground-700 hover:text-primary-600 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-foreground-700 hover:text-primary-600 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <a
              href="/#register"
              onClick={(e) => handleHashClick(e, 'register')}
              className="hidden md:inline-flex bg-primary-500 text-background-50 px-6 py-2.5 rounded-lg hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer text-sm font-semibold"
            >
              {t('nav.tryFree')}
            </a>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
              aria-label="Open menu"
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>
      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
    </>
  );
}